import React, { useState, useEffect } from 'react';

const QuickVehicleForm = ({ customerId, onSubmit, onCancel, loading, initialData }) => {
  const [formData, setFormData] = useState({
    brand: '',         // -> name
    model: '',         // -> objectives
    year: '',          // -> budget_max
    license_plate: '', // -> description (notes)
    fuel_type: '',     // -> actions (csv in UI, array in submit)
    mileage: '',       // -> budget_min
    internal_code: '', // -> internal_code
    status: 'draft',   // -> status (draft|active|paused|done)
    preview_url: '',
    preview_label: '',
    preview_embed: false
  });

  const [errors, setErrors] = useState({});

  // Actions marketing (stockées sous forme de chaîne jointe dans fuel_type)
  const actionsList = ['Site web','E-commerce','SEO','SEA','Branding','Design','Développement','Maintenance','Formation','Licence','Autre'];
  const STATUS_OPTIONS = [
    { value: 'draft', label: 'Brouillon' },
    { value: 'active', label: 'Actif' },
    { value: 'paused', label: 'En pause' },
    { value: 'done', label: 'Terminé' },
  ];
  const currentYear = new Date().getFullYear();

  const genCode = () => `PRJ-${new Date().getFullYear()}-${String(Math.floor(Math.random()*10000)).padStart(4,'0')}`;
  useEffect(() => {
    if (!initialData?.id && !formData.internal_code) {
      setFormData(prev => ({ ...prev, internal_code: genCode() }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData?.id]);

  // Prefill when editing
  useEffect(() => {
    if (!initialData?.id) return;
    const actions = Array.isArray(initialData.actions) ? initialData.actions.join(', ') : (initialData.actions || '');
    setFormData(prev => ({
      ...prev,
      brand: initialData.name || '',
      model: initialData.objectives || '',
      year: initialData.budget_max ?? '',
      license_plate: initialData.description || '',
      fuel_type: actions,
      mileage: initialData.budget_min ?? '',
      internal_code: initialData.internal_code || prev.internal_code,
      status: initialData.status || prev.status || 'draft',
      preview_url: initialData.preview_url || '',
      preview_label: initialData.preview_label || '',
      preview_embed: Boolean(initialData.preview_embed),
    }));
  }, [initialData?.id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.brand.trim()) newErrors.brand = 'Le nom du projet est requis';
    const minVal = formData.mileage !== '' && formData.mileage !== null ? Number(formData.mileage) : null;
    const maxVal = formData.year !== '' && formData.year !== null ? Number(formData.year) : null;
    if (minVal !== null && (isNaN(minVal) || minVal < 0)) newErrors.mileage = 'Le budget min doit être un nombre positif';
    if (maxVal !== null && (isNaN(maxVal) || maxVal < 0)) newErrors.year = 'Le budget max doit être un nombre positif';
    if (minVal !== null && maxVal !== null && maxVal < minVal) newErrors.year = 'Le budget max doit être ≥ au min';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    try {
      const actionsArr = (formData.fuel_type || '')
        .split(',')
        .map(t => t.trim())
        .filter(Boolean);
      const projectData = {
        name: formData.brand.trim(),
        actions: actionsArr,
        objectives: formData.model || '',
        budget_min: formData.mileage !== '' ? parseFloat(formData.mileage) : null,
        budget_max: formData.year !== '' ? parseFloat(formData.year) : null,
        internal_code: formData.internal_code ? formData.internal_code : undefined,
        description: formData.license_plate || '',
        status: (formData.status || 'draft')
      };
      if (formData.preview_url) projectData.preview_url = formData.preview_url.trim();
      if (formData.preview_label) projectData.preview_label = formData.preview_label.trim();
      if (formData.preview_embed) projectData.preview_embed = true;
      await onSubmit(projectData);
    } catch (error) {
      console.error('Erreur lors de la création du véhicule:', error);
      const pbErr = error?.data?.data;
      if (pbErr?.internal_code?.message) {
        setErrors(prev => ({ ...prev, internal_code: 'Ce code interne existe déjà. Choisissez un autre code.' }));
      }
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content quick-vehicle-modal">
        <div className="modal-header">
          <h3>{initialData?.id ? 'Modifier le projet' : 'Nouveau projet'}</h3>
          <button className="close-btn" onClick={onCancel} type="button">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="quick-vehicle-form">
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="brand">Nom du projet *</label>
              <input type="text" id="brand" name="brand" value={formData.brand} onChange={handleChange} className={errors.brand ? 'error' : ''} placeholder="Ex: Site vitrine WDB, Refonte e‑commerce" autoFocus />
              {errors.brand && <span className="error-message">{errors.brand}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="internal_code">Code interne</label>
              <input type="text" id="internal_code" name="internal_code" value={formData.internal_code} onChange={handleChange} placeholder="EX: PRJ-2025-001" className={errors.internal_code ? 'error' : ''} />
              {errors.internal_code && <span className="error-message">{errors.internal_code}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="status">Statut</label>
              <select id="status" name="status" value={formData.status} onChange={handleChange}>
                {STATUS_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label htmlFor="model">Objectifs</label>
              <textarea id="model" name="model" value={formData.model} onChange={handleChange} placeholder="Décrivez les objectifs du projet (ex: générer des leads, augmenter les ventes, améliorer la notoriété)" rows="3" />
              {errors.model && <span className="error-message">{errors.model}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="mileage">Budget indicatif — Min (€)</label>
              <input type="number" id="mileage" name="mileage" value={formData.mileage} onChange={handleChange} className={errors.mileage ? 'error' : ''} placeholder="1000" min="0" inputMode="decimal" onWheel={(e)=>e.currentTarget.blur()} />
              {errors.mileage && <span className="error-message">{errors.mileage}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="year">Budget indicatif — Max (€)</label>
              <input type="number" id="year" name="year" value={formData.year} onChange={handleChange} className={errors.year ? 'error' : ''} placeholder="3000" min="0" inputMode="decimal" onWheel={(e)=>e.currentTarget.blur()} />
              {errors.year && <span className="error-message">{errors.year}</span>}
            </div>

            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label>Choisissez vos actions marketing</label>
              <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
                {actionsList.map(act => {
                  const selected = (formData.fuel_type||'').split(',').map(t=>t.trim()).filter(Boolean).includes(act);
                  return (
                    <label key={act} style={{ display:'inline-flex', alignItems:'center', gap:6, padding:'6px 10px', border:'1px solid var(--admin-border)', borderRadius:8, cursor:'pointer', background:selected?'rgba(79,70,229,0.12)':'var(--admin-panel-2)' }}>
                      <input type="checkbox" checked={selected} onChange={() => {
                        const arr = (formData.fuel_type||'').split(',').map(t=>t.trim()).filter(Boolean);
                        const next = selected ? arr.filter(a=>a!==act) : [...new Set([...arr, act])];
                        setFormData(prev => ({ ...prev, fuel_type: next.join(', ') }));
                      }} />
                      <span>{act}</span>
                    </label>
                  );
                })}
              </div>
            </div>

            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label htmlFor="license_plate">Notes complémentaires</label>
              <textarea id="license_plate" name="license_plate" value={formData.license_plate} onChange={handleChange} placeholder="Ajoutez des précisions (contexte, délais, références, contraintes, etc.)" rows="3" />
            </div>

            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label htmlFor="preview_url">URL de maquette / prévisualisation</label>
              <input type="url" id="preview_url" name="preview_url" value={formData.preview_url} onChange={handleChange} placeholder="https://..." />
            </div>

            <div className="form-group">
              <label htmlFor="preview_label">Libellé de la preview</label>
              <input type="text" id="preview_label" name="preview_label" value={formData.preview_label} onChange={handleChange} placeholder="Ex: Maquette Figma" />
            </div>

            <div className="form-group" style={{ display:'flex', alignItems:'center', gap:8 }}>
              <label htmlFor="preview_embed" style={{ margin:0 }}>Aperçu intégré</label>
              <input type="checkbox" id="preview_embed" name="preview_embed" checked={formData.preview_embed} onChange={(e)=>setFormData(prev=>({...prev, preview_embed: e.target.checked}))} />
            </div>
          </div>

          <div style={{ color:'var(--admin-muted)', fontSize:'.8rem', marginTop:4 }}>Seuls les champs avec * sont obligatoires. Vous pourrez modifier vos informations ensuite.</div>

          <div className="form-actions">
            <button type="button" onClick={onCancel} className="btn-secondary">Annuler</button>
            <button type="submit" disabled={loading} className="btn-primary">{loading ? (initialData?.id ? 'Mise à jour...' : 'Création...') : (initialData?.id ? 'Mettre à jour' : 'Créer Projet')}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default QuickVehicleForm;
